import { createFeature, createReducer, on } from '@ngrx/store';
import { OrganizationMipsSubmissionStatus } from '@models/mips-submission-block.model';
import { MipsSubmissionBlockActions } from '@store/actions/mips-submission-block/mips-submission-block.actions';

export const mipsSubmissionBlockFeatureKey = 'mipsSubmissionBlock';

export interface State {
  statuses: OrganizationMipsSubmissionStatus[];
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
}

export const initialState: State = {
  statuses: [],
  isLoading: false,
  isSaving: false,
  error: null,
};

function mergeStatuses(
  existingStatuses: OrganizationMipsSubmissionStatus[],
  updatedStatuses: OrganizationMipsSubmissionStatus[]
): OrganizationMipsSubmissionStatus[] {
  const updatedById = new Map(updatedStatuses.map((status) => [status.organizationId, status]));
  const merged = existingStatuses.map((existing) => updatedById.get(existing.organizationId) ?? existing);
  const existingIds = new Set(existingStatuses.map((existing) => existing.organizationId));
  const newEntries = updatedStatuses.filter((status) => !existingIds.has(status.organizationId));
  return [...merged, ...newEntries];
}

export const reducer = createReducer(
  initialState,

  on(MipsSubmissionBlockActions.loadOrganizationStatuses, (state) => ({
    ...state,
    isLoading: true,
    error: null,
  })),
  on(MipsSubmissionBlockActions.loadOrganizationStatusesSuccess, (state, { statuses }) => ({
    ...state,
    statuses,
    isLoading: false,
    error: null,
  })),
  on(MipsSubmissionBlockActions.loadOrganizationStatusesFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),

  on(MipsSubmissionBlockActions.blockOrganization, (state) => ({
    ...state,
    isSaving: true,
    error: null,
  })),
  on(MipsSubmissionBlockActions.blockOrganizationSuccess, (state, { status }) => ({
    ...state,
    isSaving: false,
    statuses: mergeStatuses(state.statuses, [status]),
    error: null,
  })),
  on(MipsSubmissionBlockActions.blockOrganizationFailure, (state, { error }) => ({
    ...state,
    isSaving: false,
    error,
  })),

  on(MipsSubmissionBlockActions.unblockOrganization, (state) => ({
    ...state,
    isSaving: true,
    error: null,
  })),
  on(MipsSubmissionBlockActions.unblockOrganizationSuccess, (state, { organizationId }) => ({
    ...state,
    isSaving: false,
    statuses: state.statuses.map((existing) =>
      existing.organizationId === organizationId
        ? { ...existing, isBlocked: false, blockedByUserName: null, reasonCodes: [] }
        : existing
    ),
    error: null,
  })),
  on(MipsSubmissionBlockActions.unblockOrganizationFailure, (state, { error }) => ({
    ...state,
    isSaving: false,
    error,
  })),

  on(MipsSubmissionBlockActions.bulkBlockOrganizations, (state) => ({
    ...state,
    isSaving: true,
    error: null,
  })),
  on(MipsSubmissionBlockActions.bulkBlockOrganizationsSuccess, (state, { statuses }) => ({
    ...state,
    isSaving: false,
    statuses: mergeStatuses(state.statuses, statuses),
    error: null,
  })),
  on(MipsSubmissionBlockActions.bulkBlockOrganizationsFailure, (state, { error }) => ({
    ...state,
    isSaving: false,
    error,
  })),
);

export const mipsSubmissionBlockFeature = createFeature({
  name: mipsSubmissionBlockFeatureKey,
  reducer,
});
